/**
 * AuthScreenCmsPage — superadmin editor for the auth screens' brand panel.
 *
 * Sits at /superadmin/auth-screen. Edits the singleton AuthPanelContent
 * row via GET/PUT /superadmin/auth-panel/. Changes go live immediately
 * (no draft/publish split — the panel is a handful of short strings).
 *
 * Per language (EN / AR):
 *   - tagline (multi-line; one rendered line per row)
 *   - accent line (highlighted in brand-bright on the real screen)
 *   - up to 6 value-prop bullets
 * Plus a global enable switch — when off, the auth screens fall back to
 * their built-in copy.
 *
 * Each language column includes a live preview rendered with the same
 * gradient/colors as the real panel (AuthShell.jsx BrandPanel).
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineArrowLeft,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
} from 'react-icons/hi2'
import { authPanelAdminAPI } from '../../services/authPanel'
import ovacsLogo from '../../assets/images/logos/ovacs-logo.svg'

const MAX_BULLETS = 6

const EMPTY_BLOCK = { tagline: '', tagline_accent: '', bullets: [] }

function FlashMessage({ tone, children, onDismiss }) {
  const tones = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error:   'bg-red-50 border-red-200 text-red-800',
    info:    'bg-blue-50 border-blue-200 text-blue-800',
  }
  const Icon = tone === 'success' ? HiOutlineCheckCircle : HiOutlineExclamationTriangle
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${tones[tone] || tones.info} mb-4`}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-xs opacity-70 hover:opacity-100">Dismiss</button>
      )}
    </div>
  )
}

/** Mini render of the real brand panel so the admin sees what users get. */
function PanelPreview({ block, dir }) {
  const lines = (block.tagline || '').split('\n').map((s) => s.trim()).filter(Boolean)
  return (
    <div
      dir={dir}
      className="relative overflow-hidden rounded-xl p-5 text-white bg-gradient-to-br from-[#0a3540] to-[#125064] min-h-[15rem] flex flex-col justify-between"
    >
      <div className="absolute -inset-1/2 pointer-events-none bg-[repeating-linear-gradient(115deg,rgba(255,255,255,0.04)_0px,rgba(255,255,255,0.04)_1px,transparent_1px,transparent_60px)]" />
      <img src={ovacsLogo} alt="" className="relative z-10 h-7 w-auto self-start" />
      <div className="relative z-10 my-3">
        {lines.map((line) => (
          <span key={line} className="block text-lg font-semibold leading-snug">{line}</span>
        ))}
        {block.tagline_accent && (
          <span className="block text-lg font-semibold leading-snug text-[#01DCBA]">{block.tagline_accent}</span>
        )}
      </div>
      <ul className="relative z-10 space-y-1.5">
        {(block.bullets || []).map((b, i) => (
          <li key={`${b}-${i}`} className="flex items-center gap-2 text-xs text-white/85">
            <HiOutlineCheckCircle className="w-3.5 h-3.5 flex-shrink-0 text-[#01DCBA]" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** One language's editor column. */
function LanguageEditor({ title, dir, block, onChange }) {
  const bullets = block.bullets || []

  const setBullet = (i, value) => {
    const next = bullets.slice()
    next[i] = value
    onChange({ ...block, bullets: next })
  }
  const removeBullet = (i) => {
    onChange({ ...block, bullets: bullets.filter((_, idx) => idx !== i) })
  }
  const moveBullet = (i, delta) => {
    const j = i + delta
    if (j < 0 || j >= bullets.length) return
    const next = bullets.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange({ ...block, bullets: next })
  }
  const addBullet = () => {
    if (bullets.length >= MAX_BULLETS) return
    onChange({ ...block, bullets: [...bullets, ''] })
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          Tagline <span className="text-gray-400">(one line per row)</span>
        </label>
        <textarea
          dir={dir}
          rows={3}
          value={block.tagline}
          onChange={(e) => onChange({ ...block, tagline: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29AECB]/40 focus:border-[#29AECB]"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          Accent line <span className="text-gray-400">(highlighted in teal)</span>
        </label>
        <input
          dir={dir}
          type="text"
          maxLength={120}
          value={block.tagline_accent}
          onChange={(e) => onChange({ ...block, tagline_accent: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29AECB]/40 focus:border-[#29AECB]"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          Bullets <span className="text-gray-400">({bullets.length}/{MAX_BULLETS})</span>
        </label>
        <div className="space-y-2">
          {bullets.map((bullet, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                dir={dir}
                type="text"
                maxLength={200}
                value={bullet}
                onChange={(e) => setBullet(i, e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29AECB]/40 focus:border-[#29AECB]"
              />
              <button
                type="button"
                onClick={() => moveBullet(i, -1)}
                disabled={i === 0}
                className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                aria-label="Move up"
              >
                <HiOutlineArrowUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => moveBullet(i, 1)}
                disabled={i === bullets.length - 1}
                className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                aria-label="Move down"
              >
                <HiOutlineArrowDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => removeBullet(i)}
                className="p-1.5 text-gray-400 hover:text-red-600"
                aria-label="Remove bullet"
              >
                <HiOutlineTrash className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addBullet}
          disabled={bullets.length >= MAX_BULLETS}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#29AECB] border border-[#29AECB]/40 rounded-lg hover:bg-[#29AECB]/5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <HiOutlinePlus className="w-3.5 h-3.5" />
          Add bullet
        </button>
      </div>

      <div>
        <p className="text-xs font-medium text-gray-600 mb-1.5">Live preview</p>
        <PanelPreview block={block} dir={dir} />
      </div>
    </div>
  )
}

export default function AuthScreenCmsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [flash, setFlash] = useState(null)

  const [isEnabled, setIsEnabled] = useState(true)
  const [en, setEn] = useState(EMPTY_BLOCK)
  const [ar, setAr] = useState(EMPTY_BLOCK)
  const [snapshot, setSnapshot] = useState(null)
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null })

  const applyServerState = (data) => {
    const nextEn = { ...EMPTY_BLOCK, ...(data.content?.en || {}) }
    const nextAr = { ...EMPTY_BLOCK, ...(data.content?.ar || {}) }
    setIsEnabled(data.is_enabled !== false)
    setEn(nextEn)
    setAr(nextAr)
    setMeta({ updated_at: data.updated_at, updated_by: data.updated_by })
    setSnapshot(JSON.stringify({ isEnabled: data.is_enabled !== false, en: nextEn, ar: nextAr }))
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await authPanelAdminAPI.get()
        if (cancelled) return
        if (res.success) {
          applyServerState(res.data?.data || {})
        } else {
          setLoadError(res.error?.message || 'Failed to load auth panel content')
        }
      } catch {
        if (!cancelled) setLoadError('Network error — please refresh and try again')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const isDirty = useMemo(
    () => snapshot !== null && JSON.stringify({ isEnabled, en, ar }) !== snapshot,
    [snapshot, isEnabled, en, ar]
  )

  const handleSave = async () => {
    setSaving(true)
    setFlash(null)
    try {
      const res = await authPanelAdminAPI.update({
        is_enabled: isEnabled,
        content: {
          en: { ...en, bullets: (en.bullets || []).map((b) => b.trim()).filter(Boolean) },
          ar: { ...ar, bullets: (ar.bullets || []).map((b) => b.trim()).filter(Boolean) },
        },
      })
      if (res.success) {
        applyServerState(res.data?.data || {})
        setFlash({ tone: 'success', message: 'Saved. The auth screens now show this content.' })
      } else {
        setFlash({ tone: 'error', message: res.error?.message || 'Failed to save' })
      }
    } catch {
      setFlash({ tone: 'error', message: 'Network error — please try again' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 text-sm text-gray-500">Loading auth screen content…</div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/superadmin')}
            className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100"
            aria-label="Back to dashboard"
          >
            <HiOutlineArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Auth Screens</h1>
            <p className="text-xs text-gray-500">
              Brand panel content beside the login / register / OTP forms
              {meta.updated_at && (
                <> · last saved {new Date(meta.updated_at).toLocaleString()}{meta.updated_by ? ` by ${meta.updated_by}` : ''}</>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isDirty && <span className="text-xs font-medium text-amber-600">Unsaved changes</span>}
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="px-5 py-2 text-sm font-semibold text-white bg-[#29AECB] hover:bg-[#1d9bb5] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {flash && (
        <FlashMessage tone={flash.tone} onDismiss={() => setFlash(null)}>
          {flash.message}
        </FlashMessage>
      )}
      {loadError && <FlashMessage tone="error">{loadError}</FlashMessage>}

      {/* Enable switch */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Show custom panel content</p>
          <p className="text-xs text-gray-500 mt-0.5">
            When off, the auth screens fall back to their built-in default copy.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isEnabled}
          onClick={() => setIsEnabled((v) => !v)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
            isEnabled ? 'bg-[#29AECB]' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
              isEnabled ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {/* Language editors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <LanguageEditor title="English" dir="ltr" block={en} onChange={setEn} />
        <LanguageEditor title="Arabic (العربية)" dir="rtl" block={ar} onChange={setAr} />
      </div>
    </div>
  )
}
